import { CloseCode, ColyseusSDK, type Room } from "@colyseus/sdk";

import { ROOM_NAME } from "@shared/constants";
import type { ClientMessages, JoinOptions, Phase, ServerMessages } from "@shared/types";

import type { RoomState } from "../../server/state/RoomState.ts";

/** Colyseus endpoint relative to the page origin, so one URL Mapping covers it in Discord. */
const COLYSEUS_PATH = "/colyseus";

/**
 * Inside Discord's iframe, requests made after the initial page load must go
 * through the client-side proxy at /.proxy. The SDK detects discordsays.com
 * and injects its own URL builder, but that one hardcodes an extra /colyseus
 * prefix on top of our pathname, doubling it into /.proxy/colyseus/colyseus/...
 * which 404s. This explicit builder keeps our single prefix.
 */
function buildDiscordProxyUrl(url: URL): string {
  return `${url.protocol}//${url.host}/.proxy${url.pathname}${url.search}`;
}

function inDiscordIframe(): boolean {
  return window.location.hostname.endsWith("discordsays.com");
}

/** sessionStorage key for the reconnection token; scoped per tab, so each mock seat keeps its own. */
const RECONNECT_KEY = "odds-are:reconnect";

function readToken(): string | null {
  try {
    return sessionStorage.getItem(RECONNECT_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(RECONNECT_KEY, token);
    else sessionStorage.removeItem(RECONNECT_KEY);
  } catch {
    // Storage can be unavailable inside some embedded contexts; reconnect is then best-effort.
  }
}

export type StateListener = (state: RoomState) => void;

/**
 * Thin typed wrapper around the Colyseus room: the scenes talk to this, never
 * to the SDK directly, so message names stay in one place.
 */
export class RoomClient {
  private _room: Room<unknown, RoomState> | null = null;
  private readonly _sdk: ColyseusSDK;

  constructor(readonly session: JoinOptions) {
    this._sdk = new ColyseusSDK(
      {
        hostname: window.location.hostname,
        port: Number(window.location.port) || (window.location.protocol === "https:" ? 443 : 80),
        secure: window.location.protocol === "https:",
        pathname: COLYSEUS_PATH,
      },
      // The urlBuilder must be in the options argument; in settings it is
      // silently ignored and the SDK swaps in its own double-prefixing one.
      inDiscordIframe() ? { urlBuilder: buildDiscordProxyUrl } : {},
    );
  }

  get state(): RoomState {
    if (!this._room) throw new Error("not connected");
    return this._room.state;
  }

  get myUserId(): string {
    return this.session.userId;
  }

  /**
   * A page reload (or a Discord iframe reconnect) first tries to take back the
   * seat the server is holding for us. That consumes the reservation instead
   * of stacking a new one, which matters at a full table where reserved seats
   * count toward the room limit. Resolves once the first full state landed.
   */
  async connect(): Promise<void> {
    const room = (await this._reconnect()) ?? (await this._sdk.joinOrCreate<RoomState>(ROOM_NAME, this.session));
    this._room = room;
    writeToken(room.reconnectionToken);
    room.onLeave((code) => {
      if (code === CloseCode.CONSENTED) writeToken(null);
    });
    await new Promise<void>((resolve) => room.onStateChange.once(() => resolve()));
  }

  private async _reconnect(): Promise<Room<unknown, RoomState> | null> {
    const token = readToken();
    if (!token) return null;
    try {
      return await this._sdk.reconnect<RoomState>(token);
    } catch (error) {
      console.info("[room] stored seat no longer valid, joining fresh", error);
      writeToken(null);
      return null;
    }
  }

  onStateChange(listener: StateListener): () => void {
    const room = this._requireRoom();
    room.onStateChange(listener);
    return () => room.onStateChange.remove(listener);
  }

  onMessage<K extends keyof ServerMessages>(type: K, listener: (payload: ServerMessages[K]) => void): () => void {
    return this._requireRoom().onMessage<ServerMessages[K]>(type as string, listener);
  }

  onLeave(listener: (code: number) => void): void {
    this._requireRoom().onLeave(listener);
  }

  send<K extends keyof ClientMessages>(type: K, payload: ClientMessages[K]): void {
    this._requireRoom().send(type as string, payload);
  }

  vote(targetUserId: string): void {
    this.send("vote", { targetUserId });
  }

  start(): void {
    this.send("start", {});
  }

  get phase(): Phase {
    return this.state.phase;
  }

  private _requireRoom(): Room<unknown, RoomState> {
    if (!this._room) throw new Error("not connected");
    return this._room;
  }
}
